import React, { useState, useMemo, useEffect } from 'react'
import { Flex, Typography, message } from 'antd'
import { Icon } from '@/shared/ui/Icon/Icon'
import { DarkButton, PrimaryButton } from '@/shared/ui/components/Button'
import { TransferKey } from 'antd/es/transfer/interface'
import { useBreakpoint } from '@/app/hooks/useBreakpoint'
import { useBodyScrollLock } from '@/app/hooks/useBodyScrollLock'
import { TransferSection } from '@/pages/transfer/ui/components/DataTransferCard/ui/TransferSection'
import { ReconfigModal, ConfirmModal, ConfirmDesc, ConfirmIcon } from './styled'
import { loadSourceData } from '@/shared/api/sourceApi'
import { SourceVehicle } from '@/app/types/source'
import { getSourceApi } from '@/shared/api/config'
import { useOmnicommStore } from '@/app/store/omnicommStore'
import { useAxentaStore } from '@/app/store/axentaStore'
import { useGeliosStore } from '@/app/store/geliosStore'
import { useFortMonitorStore } from '@/app/store/fortmonitorStore'
import { SYSTEM_TYPE, useSystemStore } from '@/app/store/systemStore'
import { useAuthStore } from '@/app/store/authStore'
import authAxios from '@/shared/api/authAxios'
import { useNavigate } from 'react-router-dom'
import { routes } from '@/shared/config/routes'
import { useQueryClient } from '@tanstack/react-query'
import {
  addOptimisticTask,
  syncTaskInCache,
} from '@/entities/task/model/tasksCache'
import { tasksKeys } from '@/entities/task/model/tasksKeys'
import {
  getDefaultTaskItem,
  TASK_STATUSES,
  TaskItem,
  TASK_TYPES,
} from '@/entities/task/model/taskTypes'
import {
  AXENTA_DEFAULT_URL,
  FORTMONITOR_DEFAULT_URL,
  GELIOS_DEFAULT_URL,
  OMNICOMM_DEFAULT_URL,
  WIALON_DEFAULT_URL,
} from '@/shared/config/systemUrls'
import { getSystemDisplayName } from '@/shared/lib/getSystemDisplayName'
import { transformApiTaskToTaskItem } from '@/entities/task/lib/transformTask'

const { Title } = Typography

interface ReconfigurationModalProps {
  open: boolean
  onClose: () => void
  taskNumber?: number
  vehicleIds?: string[]
}

export const ReconfigurationModal: React.FC<ReconfigurationModalProps> = ({
  open,
  onClose,
  taskNumber,
  vehicleIds = [],
}) => {
  const { isSm, isXs } = useBreakpoint()
  const isMobile = isXs || isSm
  const [objectsTargetKeys, setObjectsTargetKeys] = useState<TransferKey[]>([])
  const [loadMessages, setLoadMessages] = useState(false)
  const [messagePeriod, setMessagePeriod] = useState(30)
  const [isConfirming, setIsConfirming] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [vehiclesData, setVehiclesData] = useState<any[]>([])
  const [isLoadingVehicles, setIsLoadingVehicles] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)

  const { selectedSystem } = useSystemStore()
  const { credentials: omnicommCredentialsData } = useOmnicommStore()
  const { credentials: axentaCredentialsData } = useAxentaStore()
  const { credentials: geliosCredentialsData } = useGeliosStore()
  const { credentials: fortMonitorCredentialsData } = useFortMonitorStore()
  const { user, userPassword, currentServer, tokenAccount } = useAuthStore()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [systemToken, setSystemToken] = useState('')
  const [systemUrl, setSystemUrl] = useState('')

  useBodyScrollLock(open)

  const isOmnicomm = selectedSystem === SYSTEM_TYPE.OMNICOMM
  const isAxenta = selectedSystem === SYSTEM_TYPE.AXENTA
  const isGelios = selectedSystem === SYSTEM_TYPE.GELIOS
  const isFortMonitor = selectedSystem === SYSTEM_TYPE.FORTMONITOR
  const isWialon = selectedSystem === SYSTEM_TYPE.WIALON

  const sourceApiUrl = getSourceApi(selectedSystem)

  useEffect(() => {
    if (!open) return

    if (isWialon) {
      setSystemUrl(WIALON_DEFAULT_URL)
      if (!systemToken) {
        setSystemToken('wialon_token_placeholder')
      }
    } else if (isOmnicomm) {
      setSystemUrl(OMNICOMM_DEFAULT_URL)
    } else if (isAxenta) {
      setSystemUrl(AXENTA_DEFAULT_URL)
    } else if (isGelios) {
      setSystemUrl(GELIOS_DEFAULT_URL)
    } else if (isFortMonitor) {
      setSystemUrl(FORTMONITOR_DEFAULT_URL)
    } else {
      setSystemUrl('')
    }
  }, [
    open,
    isWialon,
    isOmnicomm,
    isAxenta,
    isGelios,
    isFortMonitor,
    systemToken,
  ])

  const getAuthParam = useMemo(() => {
    if (!open) return ''

    if (isOmnicomm && omnicommCredentialsData) {
      return JSON.stringify({
        login: omnicommCredentialsData.login,
        password: omnicommCredentialsData.password,
      })
    }
    if (isAxenta && axentaCredentialsData) {
      return JSON.stringify({
        login: axentaCredentialsData.login,
        password: axentaCredentialsData.password,
      })
    }
    if (isGelios && geliosCredentialsData) {
      return JSON.stringify({
        login: geliosCredentialsData.login,
        password: geliosCredentialsData.password,
      })
    }
    if (isFortMonitor && fortMonitorCredentialsData) {
      return JSON.stringify({
        login: fortMonitorCredentialsData.login,
        password: fortMonitorCredentialsData.password,
      })
    }
    if (isWialon && systemToken) {
      return systemToken
    }
    return ''
  }, [
    open,
    isOmnicomm,
    isAxenta,
    isGelios,
    isFortMonitor,
    isWialon,
    omnicommCredentialsData,
    axentaCredentialsData,
    geliosCredentialsData,
    fortMonitorCredentialsData,
    systemToken,
  ])

  useEffect(() => {
    if (!open) return

    if (!selectedSystem) {
      setLoadError('Система не определена')
      return
    }

    const loadVehicles = async () => {
      if (!getAuthParam) {
        setLoadError('Отсутствуют данные авторизации')
        return
      }

      if (!systemUrl) {
        setLoadError('Отсутствует URL системы')
        return
      }

      setIsLoadingVehicles(true)
      setLoadError(null)

      try {
        const response = await loadSourceData<SourceVehicle>(
          'Vehicles',
          getAuthParam,
          sourceApiUrl,
          systemUrl,
          selectedSystem
        )

        if (response.data && Array.isArray(response.data)) {
          setVehiclesData(response.data)
        } else if (response.error) {
          setLoadError(response.error)
          message.error(`Ошибка загрузки объектов: ${response.error}`)
        } else {
          setVehiclesData([])
        }
      } catch (error) {
        console.error('Ошибка при загрузке объектов:', error)
        setLoadError('Не удалось загрузить список объектов')
        message.error('Не удалось загрузить список объектов')
      } finally {
        setIsLoadingVehicles(false)
      }
    }

    loadVehicles()
  }, [open, getAuthParam, sourceApiUrl, systemUrl, selectedSystem])

  const objectsForTransfer = useMemo(() => {
    if (!open) return []
    if (!vehiclesData.length) return []

    if (vehicleIds && vehicleIds.length > 0) {
      const filteredVehicles = vehiclesData.filter((vehicle) =>
        vehicleIds.includes(vehicle.id)
      )

      return filteredVehicles.map((vehicle) => ({
        id: vehicle.id,
        name: vehicle.name || `Объект ${vehicle.id}`,
        description: vehicle.regNumber || vehicle.model || '',
      }))
    }

    return vehiclesData.map((vehicle) => ({
      id: vehicle.id,
      name: vehicle.name || `Объект ${vehicle.id}`,
      description: vehicle.regNumber || vehicle.model || '',
    }))
  }, [open, vehiclesData, vehicleIds])

  useEffect(() => {
    if (!open) return

    if (objectsForTransfer.length > 0) {
      const validIds = new Set(objectsForTransfer.map((obj) => obj.id))
      const validTargetKeys = objectsTargetKeys.filter((key) =>
        validIds.has(key as string)
      )

      if (validTargetKeys.length !== objectsTargetKeys.length) {
        setObjectsTargetKeys(validTargetKeys)
      }
    } else {
      setObjectsTargetKeys([])
    }
  }, [open, objectsForTransfer])

  const handleObjectsChange = (newTargetKeys: TransferKey[]) => {
    setObjectsTargetKeys(newTargetKeys)
  }

  const handleSelectAllObjects = () => {
    setObjectsTargetKeys(objectsForTransfer.map((item) => item.id))
  }

  const handleDeselectAllObjects = () => {
    setObjectsTargetKeys([])
  }

  const handleLoadMessagesChange = (checked: boolean) => {
    setLoadMessages(checked)
  }

  const handleMessagePeriodChange = (period: number) => {
    setMessagePeriod(period)
  }

  const handleSaveClick = () => {
    if (!hasSelectedItems) return
    setShowConfirmModal(true)
  }

  const handleConfirmReconfiguration = async () => {
    setIsConfirming(true)
    setShowConfirmModal(false)

    try {
      const subTasks = [...TASK_TYPES.RECONFIGURATION]

      const sourceAccount =
        tokenAccount || user?.username || getSystemDisplayName(selectedSystem)

      const requestData = {
        name: `Перенастройка оборудования (задача №${taskNumber})`,
        description: `Перенастройка ${objectsTargetKeys.length} объектов из системы ${selectedSystem} в ГЛОНАССSoft`,
        source: {
          id: sourceAccount,
          name: sourceAccount,
          system: selectedSystem,
          host: systemUrl,
        },
        receiver: {
          id: user?.username,
          name: user?.username || 'GlonassSoft',
          system: '',
          host: currentServer,
        },
        messagePeriod: loadMessages ? messagePeriod : 0,
        subTasks,
        sourceFilter: {
          agentIds: null,
          userIds: [],
          vehicleIds: objectsTargetKeys as string[],
          driverIds: [],
          zoneIds: [],
          retranslationIds: [],
        },
        sourceAuth: {
          auth: getAuthParam,
        },
        receiverAuth: {
          loginPassword: {
            login: user?.username || '',
            password: userPassword || '',
          },
          agentId: '',
          useAsRoot: true,
        },
      }

      const response = await authAxios.post('/public/Tasks', requestData, {
        timeout: 30000,
      })

      if (response.status === 200 || response.status === 201) {
        const taskId = response.data

        // Создаем временную задачу
        const tempTask: TaskItem = {
          ...getDefaultTaskItem(),
          id: taskId,
          status: TASK_STATUSES.CREATED,
          createdAt: new Date().toLocaleString('ru-RU'),
          startedAt: 'Не начата',
          completedAt: 'Не завершена',
          uploadAccount: sourceAccount,
          downloadAccount: user?.username || 'GlonassSoft',
          vehicleIds: objectsTargetKeys as string[],
          isReconfiguration: true,
        }

        if (user?.username) {
          addOptimisticTask(queryClient, tasksKeys.my(user.username), tempTask)
        }

        try {
          const taskResponse = await authAxios.get(
            `/public/Tasks/byId/${taskId}`,
            {
              timeout: 30000,
            }
          )

          const transformedTask = transformApiTaskToTaskItem(taskResponse.data)

          if (transformedTask && user?.username) {
            syncTaskInCache(
              queryClient,
              tasksKeys.my(user.username),
              transformedTask
            )
          }
        } catch (loadError) {
          console.error('Ошибка при загрузке данных задачи:', loadError)
        }

        message.success('Задача на перенастройку оборудования создана')

        onClose()

        setTimeout(() => {
          navigate(routes.tasks)
        }, 1500)
      } else {
        message.warning(
          `Перенастройка запущена со статусом: ${response.status}`
        )
      }
    } catch (error) {
      console.error('Ошибка при перенастройке:', error)
      message.error('Ошибка при создании задачи на перенастройку оборудования')
    } finally {
      setIsConfirming(false)
    }
  }

  const handleCancelConfirm = () => {
    setShowConfirmModal(false)
  }

  const hasSelectedItems = objectsTargetKeys.length > 0
  const isConfirmDisabled =
    !hasSelectedItems || isConfirming || isLoadingVehicles

  if (!open) {
    return null
  }

  if (!selectedSystem) {
    return null
  }

  return (
    <>
      <ReconfigModal
        title={
          <Title level={4}>
            Перенастройка оборудования. Задача №{taskNumber}
          </Title>
        }
        open={open}
        onCancel={onClose}
        footer={null}
        width={isMobile ? '95%' : 1100}
        centered
        closeIcon={<Icon name="closed_modal" size={16} />}
      >
        <Flex vertical gap={24}>
          {isLoadingVehicles ? (
            <Flex justify="center" align="center" style={{ minHeight: 400 }}>
              <Icon name="spinner" size={32} spin />
              <Typography.Text style={{ marginLeft: 12 }}>
                Загрузка объектов...
              </Typography.Text>
            </Flex>
          ) : loadError ? (
            <Flex vertical gap={16} align="center" style={{ minHeight: 400 }}>
              <Icon name="warning_alert" size={32} />
              <Typography.Text type="danger">{loadError}</Typography.Text>
              <DarkButton size="small" onClick={onClose}>
                Закрыть
              </DarkButton>
            </Flex>
          ) : (
            <>
              <TransferSection
                title="Объекты"
                dataSource={objectsForTransfer}
                value={objectsTargetKeys}
                onChange={handleObjectsChange}
                onSelectAll={handleSelectAllObjects}
                onDeselectAll={handleDeselectAllObjects}
                defaultOpen
                loadMessages={loadMessages}
                onLoadMessagesChange={handleLoadMessagesChange}
                messagePeriod={messagePeriod}
                onMessagePeriodChange={handleMessagePeriodChange}
                height={isMobile ? 354 : 354}
                idField="id"
                nameField="name"
              />
              <Flex justify="flex-end" gap={16}>
                <DarkButton
                  size="small"
                  onClick={onClose}
                  disabled={isConfirming}
                >
                  Отменить
                </DarkButton>
                <PrimaryButton
                  size="small"
                  onClick={handleSaveClick}
                  disabled={isConfirmDisabled}
                >
                  Сохранить
                </PrimaryButton>
              </Flex>
            </>
          )}
        </Flex>
      </ReconfigModal>

      <ConfirmModal
        open={showConfirmModal}
        onCancel={handleCancelConfirm}
        footer={null}
        width={350}
        centered
        closable={false}
      >
        <Flex vertical gap={12}>
          <Flex gap={8}>
            <ConfirmIcon name="warning_alert" size={16} />
            <ConfirmDesc>
              Внимание. После прохождения процедуры перенастройки адреса сервиса
              терминалы будут посылать данные в систему ГЛОНАССSoft.
            </ConfirmDesc>
          </Flex>
          <Flex justify="flex-end" gap={8}>
            <DarkButton size="small" onClick={handleCancelConfirm}>
              Отменить
            </DarkButton>
            <PrimaryButton
              size="small"
              onClick={handleConfirmReconfiguration}
              loading={isConfirming}
            >
              Согласен
            </PrimaryButton>
          </Flex>
        </Flex>
      </ConfirmModal>
    </>
  )
}
